import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, CircularProgress, InputBase, styled } from "@mui/material";
import { LandingPageLayout } from "fe-web-components/layout/landingPageLayout";
import {
  ExtensionStorageKeys,
  ExtensionStorageKeyTypes,
  ExtensionStorageValueTypes,
} from "fe-web-components/chromeStorage/ExtensionStorageKeys";
import {
  addGlobalMessageExtensionListener,
  createGlobalEventHandler,
  ExtensionGlobalMessageTypes,
  ExtensionGlobalSources,
  removeGlobalMessageExtensionListener,
  sendExtensionMessage,
} from "fe-web-components/chromeStorage/extenstionEventWrappers";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { SettingSwitch } from "fe-web-components/settings/presentaional/controls/settingSwitch";
import { ClinicDao } from "fe-common/graphql/dao";
import { gqlClient } from "fe-common/graphql/webClient";
import { getClinicDataFromServer } from "fe-web-components/clinicDataProvider/getClinicDataFromServer";
import debounce from "debounce-fn";
import { UserRoles } from "fe-common/graphql";
import {
  getUserRoles,
  refreshSession,
} from "user-management/src/cognito/kahunUserAuth";
//region [[ Styles ]]

const ExtensionContainerView = styled(LandingPageLayout)({
  minHeight: "100%",
  display: "flex",
  flexFlow: "column",
  alignContent: "center",
  "& .content-view": {
    alignItems: "center",
  },
});

const StorageValueInput = styled(InputBase)({
  boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.04)",
  borderRadius: "3px",
  background: "lightgray",
  width: "100%",
  "& input, & textarea": {
    color: "#5C5C5D",
    textAlign: "center",
    fontSize: "11px",
    fontWeight: 600,
  },
});

const OptionContainer = styled("div")({
  height: "calc(100vh - 202px)",
  flexFlow: "column",
  display: "flex",
  justifyContent: "flex-start",
  width: "60%",
});

const ConfigOptions = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "flex-start",
});

const ContinueButton = styled(Button)({
  border: "1px solid #AAB0B7",
  borderRadius: "8px",
  background: "#5A52C3",
  color: "white",
  margin: "15px 0",
  fontSize: "14px",
  "&:hover": {
    color: "#232323",
  },
});

const Center = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "center",
});

const SuccessContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  background: "darkseagreen",
  padding: "6px",
  border: `1px dashed #232323`,
  width: "360px",
  fontSize: "12px",
  fontWeight: 700,
  borderRadius: "20px",
  "& svg": {
    fill: "lime",
    margin: "0 5px",
  },
  color: "#232323",
});

const ErrorContainer = styled(SuccessContainer)({
  background: "palevioletred",
  "& svg": {
    fill: "darkred",
  },
});

const LogContainer = styled("div")({
  ...scrollableStyle,
  maxHeight: "500px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ExtensionContainerProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
export const ExtensionContainer = ({ ...props }: ExtensionContainerProps) => {
  const [storageValues, setStorageValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventLog, setEventLog] = useState<object[]>([]);
  const [timedOut, setTimedOut] = useState(false);
  const [loadingUserRoles, setLoadingUserRoles] = useState(true);
  const [userRoles, setUserRoles] = useState([]);

  const waiting = useRef(true);
  const clinicDao = useMemo(
    () => new ClinicDao(() => gqlClient({ "kahun-langcode": "en" })),
    [],
  );
  const [loadingKeys, setLoadingKeys] = useState({});

  const turnLoadingKey = useCallback(
    (key: ExtensionStorageKeys, value: boolean) => {
      setLoadingKeys((keys) => ({ ...keys, [key]: value }));
    },
    [],
  );

  const handleExtensionEvent = useCallback(
    (event) =>
      createGlobalEventHandler(event, ExtensionGlobalSources.WebPage, (m) => {
        setLoading(false);
        waiting.current = false;
        setEventLog((logData) => [...logData, { ...m }]);
        if (m.messageType === ExtensionGlobalMessageTypes.CurrentSettings) {
          const { settings } = m.payload;
          setStorageValues(settings.reduce((acc, v) => ({ ...acc, ...v }), {}));
        }
      }),
    [],
  );

  const tryLoading = useCallback((attempt = 0) => {
    console.log(`tryLoading - attempt no. ${attempt + 1}`);
    sendExtensionMessage(
      ExtensionGlobalMessageTypes.GetSettings,
      ExtensionGlobalSources.WebPage,
    );
    if (waiting.current && attempt < 5) {
      setTimeout(() => {
        tryLoading(attempt + 1);
      }, 1000); // Give the extension some time to load
    } else if (attempt >= 5) {
      console.log("Timed out - couldn't reach extension");
      setTimedOut(true);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadingUserRoles(true);
    refreshSession()
      .then(getUserRoles)
      .then((userRoles) => {
        console.log("User roles:", userRoles);
        setUserRoles(userRoles);
        setLoadingUserRoles(false);
      });
    addGlobalMessageExtensionListener(handleExtensionEvent);
    tryLoading();
    return () => {
      removeGlobalMessageExtensionListener(handleExtensionEvent);
    };
  }, [handleExtensionEvent, tryLoading]);

  type WaitToGetValueParams<T> = {
    key: ExtensionStorageKeys;
    dataReader: () => Promise<T>;
    resultExtractor: (T) => Record<string, any>;
  };

  const waitToGetValue: <T>(WaitToGetValueParams) => Promise<T> = useCallback(
    ({ key, dataReader, resultExtractor }) => {
      console.log("Calling Debounced waitToGetValue", key);
      turnLoadingKey(key, true);
      return dataReader()
        .then(resultExtractor)
        .then((value) => {
          setStorageValues((storageValues) => ({
            ...storageValues,
            [key]: value,
          }));
          sendExtensionMessage(
            ExtensionGlobalMessageTypes.UpdateSettings,
            ExtensionGlobalSources.WebPage,
            { [key]: value },
          );
          return value;
        })
        .finally(() => turnLoadingKey(key, false))
        .catch((e) => {
          console.error(e);
          setStorageValues((storageValues) => ({
            ...storageValues,
            [key]: "",
          }));
        });
    },
    [turnLoadingKey],
  );

  const waitToGetValueDebounced = useMemo(
    () => debounce(waitToGetValue, { wait: 2000 }),
    [waitToGetValue],
  );

  const handleValueChanged = useCallback(
    (key: ExtensionStorageKeys, e, _value = null) => {
      let value = e?.target?.value;
      if (!value) {
        value = _value;
      }

      if (!value) {
        setStorageValues((storageValues) => ({
          ...storageValues,
          [key]: "",
        }));
        return;
      }

      if (key === ExtensionStorageKeys.ClinicName) {
        waitToGetValueDebounced({
          key: ExtensionStorageKeys.ClinicId,
          dataReader: () => clinicDao.getClinicByName(value.trim()),
          resultExtractor: (clinicData) => (!clinicData ? "" : clinicData.id),
        });
      } else if (key === ExtensionStorageKeys.ClinicId) {
        waitToGetValueDebounced({
          key: ExtensionStorageKeys.ClinicName,
          dataReader: () => getClinicDataFromServer(clinicDao, value.trim()),
          resultExtractor: (clinicData) => (!clinicData ? "" : clinicData.name),
        });
      }
      setStorageValues((storageValues) => ({
        ...(storageValues || {}),
        [key]: value,
      }));

      sendExtensionMessage(
        ExtensionGlobalMessageTypes.UpdateSettings,
        ExtensionGlobalSources.WebPage,
        { [key]: value },
      );
    },
    [clinicDao, waitToGetValueDebounced],
  );

  const createStorageValueChangedHandler = (key: ExtensionStorageKeys) => (
    e,
    _value = null,
  ) => {
    handleValueChanged(key, e, _value || e?.target?.value);
  };

  const closeWindow = () => {
    sendExtensionMessage(
      ExtensionGlobalMessageTypes.CloseSettingsPage,
      ExtensionGlobalSources.WebPage,
    );
  };

  const userHasRoles = (supported: UserRoles[]) =>
    userRoles?.some((urole) =>
      supported.some((srole) => urole.includes(srole)),
    );

  const showRagOption = (key: ExtensionStorageKeys) => {
    //Alaways show rag options
    return true;
    // return (
    //   (key !== ExtensionStorageKeys.DialogDemoMode &&
    //     key !== ExtensionStorageKeys.RagApp) ||
    //   (!loadingUserRoles &&
    //     userRoles([
    //       UserRoles.KahunClinicSuperAdmins,
    //       UserRoles.KahunClinicAdmins,
    //     ]))
    // );
  };

  return (
    <ExtensionContainerView>
      <h1>Welcome to Kahun Extension</h1>
      {loading && (
        <>
          Looking for extension... <CircularProgress />
          <Button
            onClick={() =>
              sendExtensionMessage(
                ExtensionGlobalMessageTypes.GetSettings,
                ExtensionGlobalSources.WebPage,
              )
            }
          >
            Refresh
          </Button>
        </>
      )}
      {timedOut && (
        <Center>
          <ErrorContainer>
            <ErrorIcon />
            Couldn't Communicate with Kahun extension. Try refreshing or calling
            support...
          </ErrorContainer>
        </Center>
      )}
      <OptionContainer>
        <ConfigOptions>
          {!loading && (
            <>
              <Center>
                <SuccessContainer>
                  <CheckCircleIcon />
                  Kahun Extension was successfully installed
                </SuccessContainer>
              </Center>
              <p>Please fill in the following settings:</p>
            </>
          )}
          {!loading &&
            storageValues &&
            Object.entries(storageValues)
              ?.filter(
                ([key]) =>
                  ExtensionStorageKeyTypes[key] &&
                  ExtensionStorageKeyTypes[key] !==
                    ExtensionStorageValueTypes.Hidden &&
                  showRagOption(key as ExtensionStorageKeys),
              )
              .map?.(([key, value]) => (
                <p key={key}>
                  <b>
                    {Object.entries(ExtensionStorageKeys || {}).find?.(
                      ([, v]) => key === v,
                    )?.[0] || key}
                  </b>
                  {(!ExtensionStorageKeyTypes[key] ||
                    ExtensionStorageKeyTypes[key] ===
                      ExtensionStorageValueTypes.String) && (
                    <>
                      <StorageValueInput
                        value={value}
                        onChange={createStorageValueChangedHandler(
                          key as ExtensionStorageKeys,
                        )}
                        disabled={loadingKeys[key]}
                      />
                      {!!loadingKeys[key] && <CircularProgress size={10} />}
                    </>
                  )}
                  {ExtensionStorageKeyTypes[key] ===
                    ExtensionStorageValueTypes.Boolean && (
                    <SettingSwitch
                      checked={value === "true"}
                      disabled={loading}
                      onChange={(e, checked) =>
                        createStorageValueChangedHandler(
                          key as ExtensionStorageKeys,
                        )(e, checked ? "true" : "false")
                      }
                    />
                  )}
                </p>
              ))}
          <p>
            Once filled you can close this window and log to your EHR provider
            page to start using it
          </p>
          {!loading && (
            <ContinueButton onClick={closeWindow}>CONTINUE</ContinueButton>
          )}
        </ConfigOptions>
        {eventLog &&
          storageValues &&
          storageValues[ExtensionStorageKeys.Debug] === "true" &&
          Object.entries(eventLog)?.length > 0 && (
            <LogContainer>
              <b>Events:</b>
              <pre>
                <code>{JSON.stringify(eventLog, null, 4)}</code>
              </pre>
            </LogContainer>
          )}
      </OptionContainer>
    </ExtensionContainerView>
  );
};
